<template>
  <div class="w-full mx-auto px-2 sm:px-0 py-1 sm:py-4 pb-4">
    <!-- Success Completion Card -->
    <div
      class=" bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 text-center flex flex-col items-center justify-center">
      <!-- Animated checkmark ring -->

      <div
        class="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mb-3 ring-4 ring-emerald-50/60 shrink-0">
        <v-icon size="32" color="emerald darken-1">mdi-check-circle</v-icon>
      </div>
      <h1 class="text-base sm:text-xl font-black text-gray-900 mb-1 leading-tight">
        Task Completed Successfully!
      </h1>
      <p class="text-[11px] sm:text-xs text-gray-500 max-w-xs mb-3.5 leading-tight">
        Great job! All steps and visit records have been submitted and synchronized.
      </p>

      <!-- Task Summary Strip -->
      <div v-if="taskData"
        class="w-full bg-gray-50/90 rounded-xl p-2.5 sm:p-3.5 border border-gray-100 text-left mb-4 space-y-1.5 text-xs">
        <div class="flex items-center justify-between pb-1 border-b border-gray-200/80">
          <span class="text-gray-500 text-[11px]">Customer</span>
          <span class="font-bold text-gray-900 text-right text-[11px] truncate max-w-[180px]">{{ customerName }}</span>
        </div>
        <div class="flex items-center justify-between pb-1 border-b border-gray-200/80">
          <span class="text-gray-500 text-[11px]">Case / Task</span>
          <span class="font-bold text-indigo-700 font-mono text-right text-[11px]">{{ caseNumber }}</span>
        </div>
        <div class="flex items-center justify-between pb-1 border-b border-gray-200/80">
          <span class="text-gray-500 text-[11px]">Completed At</span>
          <span class="font-bold text-gray-800 text-right text-[11px]">{{ completionTime }}</span>
        </div>
        <div v-if="taskData.pos || taskData.collectionDetails?.principalOutstanding"
          class="flex items-center justify-between">
          <span class="text-gray-500 text-[11px]">Outstanding POS</span>
          <span class="font-black text-emerald-700 text-right text-[11px]">₹ {{ formatAmount(taskData.pos ||
            taskData.collectionDetails?.principalOutstanding) }}</span>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="w-full space-y-2">
        <!-- Return to Dashboard Button -->
        <v-btn depressed block small :color="$vuetify.theme.currentTheme.employeePrimary" class="h-9 rounded-lg font-bold text-[11px] white--text shadow-xs"
          @click="goToDashboard">
          <v-icon left x-small>mdi-home-outline</v-icon>
          Return to Dashboard
        </v-btn>

        <!-- WhatsApp Share Button -->
        <v-btn depressed block small color="#25D366" class="h-9 rounded-lg font-bold text-[11px] white--text shadow-xs"
          style="background-color: #25D366 !important; color: #ffffff !important;"
          @click="shareOnWhatsapp">
          <v-icon left x-small color="white">mdi-whatsapp</v-icon>
          Share on WhatsApp
        </v-btn>

        <!-- Notify Customer via Template Button -->
        <v-btn v-if="whatsappTemplates.length > 0" text block small :color="$vuetify.theme.currentTheme.employeePrimary"
          class="h-7 rounded-lg text-[10px] font-bold" @click="templateModalOpen = true">
          <v-icon left x-small>mdi-message-text-outline</v-icon>
          <span class="truncate">Notify Customer via Template</span>
        </v-btn>
      </div>
    </div>

    <!-- WhatsApp Templates Dialog Modal -->
    <v-dialog v-model="templateModalOpen" width="94%" max-width="440">
      <v-card class="rounded-2xl pa-4 sm:pa-5">
        <div class="flex items-center justify-between pb-3 border-b border-gray-100 gap-2 mb-3">
          <h3 class="text-sm sm:text-base font-bold text-gray-900 mb-0">Select WhatsApp Template</h3>
          <button type="button" class="text-gray-400 hover:text-gray-600 border-none bg-transparent cursor-pointer p-1"
            @click="templateModalOpen = false">
            <v-icon small color="grey">mdi-close</v-icon>
          </button>
        </div>

        <div class="space-y-2 max-h-64 overflow-y-auto pr-1">
          <div v-for="tpl in whatsappTemplates" :key="tpl._id || tpl.id" @click="selectedTemplateId = tpl._id || tpl.id"
            :class="selectedTemplateId === (tpl._id || tpl.id)
              ? 'border-indigo-500 bg-indigo-50/50 shadow-xs'
              : 'border-gray-200 hover:bg-gray-50'"
            class="p-2.5 sm:p-3 rounded-xl border border-solid cursor-pointer transition-all">
            <div class="flex items-center justify-between mb-1">
              <span class="font-bold text-xs text-gray-900">{{ tpl.templateName || 'Notification' }}</span>
              <v-icon v-if="selectedTemplateId === (tpl._id || tpl.id)" small color="indigo">mdi-check-circle</v-icon>
            </div>
            <p class="text-[11px] text-gray-600 line-clamp-2 mb-0">{{ tpl.templateText || tpl.body }}</p>
          </div>
        </div>

        <v-card-actions class="justify-end gap-2 mt-3 px-0 pb-0 pt-2 border-t border-gray-100">
          <v-btn text small class="rounded-lg text-xs" @click="templateModalOpen = false">Cancel</v-btn>
          <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-lg text-xs font-bold px-4"
            :disabled="!selectedTemplateId" :loading="sendingTemplate" @click="sendCustomerTemplate">
            Send Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- WhatsApp Share Preview & Edit Dialog Modal -->
    <v-dialog v-model="shareModalOpen" width="94%" max-width="440">
      <v-card class="rounded-2xl pa-4 sm:pa-5 overflow-hidden">
        <div class="flex items-center justify-between pb-3 border-b border-gray-100 gap-2 mb-3">
          <div class="flex items-center gap-2.5 min-w-0">
            <div class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style="background-color: #e8faf0 !important;">
              <v-icon size="20" color="#128C7E">mdi-whatsapp</v-icon>
            </div>
            <div class="min-w-0">
              <h3 class="text-xs sm:text-sm font-bold text-gray-900 mb-0 truncate">Share Visit Update</h3>
              <p class="text-[11px] text-gray-500 mb-0 truncate">Review or edit message before sharing</p>
            </div>
          </div>
          <button type="button" class="text-gray-400 hover:text-gray-600 border-none bg-transparent cursor-pointer p-1 shrink-0 -mr-1"
            @click="shareModalOpen = false">
            <v-icon small color="grey">mdi-close</v-icon>
          </button>
        </div>

        <div class="space-y-2">
          <v-textarea v-model="whatsappMessage" rows="5" auto-grow outlined dense hide-details
            class="rounded-xl text-xs font-mono"></v-textarea>
        </div>

        <div class="flex flex-col gap-2 mt-3.5 pt-3 border-t border-gray-100">
          <!-- Full-width prominent WhatsApp button -->
          <v-btn depressed block color="#25D366" class="white--text rounded-xl text-xs font-bold h-10 shadow-xs"
            style="background-color: #25D366 !important; color: #ffffff !important;"
            @click="sendWhatsappMessage">
            <v-icon left small color="white">mdi-whatsapp</v-icon>
            Share on WhatsApp
          </v-btn>

          <!-- 50/50 split secondary buttons -->
          <div class="flex items-center gap-2">
            <v-btn outlined small color="grey darken-2" class="flex-1 rounded-xl h-8.5 text-xs font-semibold border-gray-200"
              @click="copyWhatsappMessage">
              <v-icon left x-small>mdi-content-copy</v-icon>
              Copy Text
            </v-btn>

            <v-btn text small class="flex-1 rounded-xl h-8.5 text-xs text-gray-600 font-semibold"
              @click="shareModalOpen = false">
              Cancel
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  getTaskById,
  getCollectionCaseById,
  updateTaskStatus,
  updateCaseStatus,
  getWhatsappTemplates,
  sendWhatsappNotification
} from '@/app/services/dashboard'
import { sendStepLocation } from '@/app/utils/taskFlow'
import { convertNumberToAmount } from '@/app/utils/helpers'

export default {
  name: 'AppTaskCompleted',
  data() {
    return {
      taskId: this.$route.params.taskId,
      isCollection: this.$route.query.isCollection === 'true' || this.$route.query.isCollection === true,
      taskData: null,
      whatsappTemplates: [],
      templateModalOpen: false,
      shareModalOpen: false,
      whatsappMessage: '',
      selectedTemplateId: null,
      sendingTemplate: false,
      completionTime: dayjs().format('DD MMM YYYY, hh:mm A')
    }
  },
  computed: {
    customerName() {
      const caseData = this.taskData?.case || (this.taskData?.tasks ? this.taskData : null) || this.taskData
      const task = this.taskData?.tasks?.[0] || (this.taskData?.case ? this.taskData : null) || this.taskData
      return (
        caseData?.customer?.customerFullName ||
        task?.customer?.customerFullName ||
        caseData?.customer?.name ||
        task?.customer?.name ||
        task?.customerFullName ||
        task?.taskTitle ||
        'Customer'
      )
    },
    caseNumber() {
      const caseData = this.taskData?.case || (this.taskData?.tasks ? this.taskData : null) || this.taskData
      const task = this.taskData?.tasks?.[0] || (this.taskData?.case ? this.taskData : null) || this.taskData
      const no = caseData?.caseNo || task?.caseNo || task?.collectionDetails?.caseNo || task?.caseNumber
      return no ? `${no}` : (task?.taskTitle || 'Task')
    }
  },
  mounted() {
    this.finalizeTaskCompletion()
  },
  beforeDestroy() {
    this.cleanupTaskLocalStorage()
  },
  methods: {
    formatAmount(amount) {
      return convertNumberToAmount(amount)
    },
    openGoogleMaps(address) {
      if (!address) return ''
      const encodedAddress = encodeURIComponent(address)
      return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
    },
    buildWhatsappMessage() {
      const user =
        this.$storage?.getUniversal('user')?.user ||
        this.$storage?.getUniversal('user') ||
        JSON.parse(localStorage.getItem('user') || '{}')
      const innerUser = user.user || user

      const nameParts = [
        innerUser.firstName || innerUser.first_name,
        innerUser.middleName || innerUser.middle_name,
        innerUser.lastName || innerUser.last_name
      ].filter(Boolean)
      const userName = nameParts.length
        ? nameParts.join(' ')
        : (innerUser.name || innerUser.fullName || innerUser.username || 'Agent')

      const rawTask = this.taskData || {}
      const task =
        (Array.isArray(rawTask.tasks) && rawTask.tasks[0]) ||
        (rawTask.case ? rawTask : rawTask)
      const caseData =
        rawTask.case ||
        (Array.isArray(rawTask.tasks) ? rawTask : rawTask)

      const customerName =
        caseData?.customer?.customerFullName ||
        task?.customer?.customerFullName ||
        caseData?.customer?.name ||
        task?.customer?.name ||
        task?.customerFullName ||
        task?.taskTitle ||
        'N/A'

      const caseNo =
        caseData?.caseNo ||
        task?.caseNo ||
        task?.collectionDetails?.caseNo ||
        task?.caseNumber ||
        task?.taskTitle ||
        ''

      const rawAddress =
        task?.additionalInfo?.taskStartData?.address ||
        caseData?.additionalInfo?.taskStartData?.address ||
        (task?.additionalInfo?.taskStartData?.coords?.latitude && task?.additionalInfo?.taskStartData?.coords?.longitude
          ? `${task.additionalInfo.taskStartData.coords.latitude}, ${task.additionalInfo.taskStartData.coords.longitude}`
          : null) ||
        localStorage.getItem(`taskAddress-${this.taskId}`) ||
        (task?._id ? localStorage.getItem(`taskAddress-${task._id}`) : null) ||
        caseData?.customer?.customerAddress ||
        task?.customer?.customerAddress ||
        task?.additionalInfo?.startJobData?.address ||
        task?.additionalInfo?.location?.address ||
        'N/A'

      const rawStartTime =
        task?.actualStartDateTime ||
        task?.additionalInfo?.taskStartData?.datetime ||
        localStorage.getItem(`taskStartTime-${this.taskId}`) ||
        (task?._id ? localStorage.getItem(`taskStartTime-${task._id}`) : null) ||
        task?.createdAt ||
        rawTask?.createdAt
      const startTime = rawStartTime
        ? dayjs(rawStartTime).format('DD/MM/YYYY hh:mm A')
        : dayjs().format('DD/MM/YYYY hh:mm A')

      const meetingNotes =
        task?.meetingDetails?.meetingNotes ||
        rawTask?.meetingDetails?.meetingNotes ||
        task?.meetingNotes ||
        task?.meetingDetails?.notes ||
        localStorage.getItem(`lmn-${this.taskId}`) ||
        localStorage.getItem(`lastMeetingNotes-${this.taskId}`) ||
        (task?._id ? localStorage.getItem(`lmn-${task._id}`) : null) ||
        (task?._id ? localStorage.getItem(`lastMeetingNotes-${task._id}`) : null) ||
        ''

      const userDefinedCaseStatus =
        caseData?.userDefinedCaseStatus ||
        task?.userDefinedCaseStatus ||
        task?.meetingDetails?.meetingOutCome ||
        task?.meetingDetails?.outcome ||
        localStorage.getItem(`lmo-${this.taskId}`) ||
        localStorage.getItem(`lastMeetingOutcome-${this.taskId}`) ||
        (task?._id ? localStorage.getItem(`lmo-${task._id}`) : null) ||
        (task?._id ? localStorage.getItem(`lastMeetingOutcome-${task._id}`) : null) ||
        'N/A'

      const s3Url = process.env.VUE_APP_S3_BASE_URL || 'https://uat-cdn.myfinfi.com/'
      const rawSelfie =
        task?.additionalInfo?.selfie?.doc_physical_path ||
        caseData?.additionalInfo?.selfie?.doc_physical_path ||
        task?.selfieUrl ||
        task?.additionalInfo?.selfieUrl ||
        task?.additionalInfo?.selfie?.url ||
        localStorage.getItem(`selfiePath-${this.taskId}`) ||
        (task?._id ? localStorage.getItem(`selfiePath-${task._id}`) : null) ||
        ''

      let imgUrl = ''
      if (rawSelfie) {
        if (/^https?:\/\//i.test(rawSelfie)) {
          imgUrl = rawSelfie
        } else {
          imgUrl = `${s3Url.replace(/\/$/, '')}/${rawSelfie.replace(/^\//, '')}`
        }
      }

      let message = `${userName}'s visit update.`

      if (customerName && customerName !== 'N/A') {
        message += ` CUSTOMER: ${customerName},`
      }

      if (rawAddress && rawAddress !== 'N/A') {
        message += `CASE: ${caseNo}, LOCATION: ${this.openGoogleMaps(rawAddress)} AT ${startTime}.
      MEETING NOTES: ${meetingNotes} ,`
      } else if (caseNo) {
        message += `CASE: ${caseNo}, AT ${startTime}.`
        if (meetingNotes) {
          message += `\n      MEETING NOTES: ${meetingNotes} ,`
        }
      }

      if (userDefinedCaseStatus && userDefinedCaseStatus !== 'N/A') {
        message += `and outcome of visit is ${userDefinedCaseStatus}.`
      }

      if (imgUrl) {
        message += `Captured image url: ${imgUrl}.`
      }

      return message
    },
    async finalizeTaskCompletion() {
      try {
        let resData = null
        try {
          const tRes = await getTaskById({ taskId: this.taskId, isCollection: this.isCollection })
          resData =
            tRes.data?.case ||
            tRes.data?.task ||
            tRes.data?.data?.case ||
            tRes.data?.data?.task ||
            tRes.data?.data ||
            tRes.data
        } catch (e) {
          if (this.isCollection) {
            const cRes = await getCollectionCaseById({ caseId: this.taskId }).catch(() => null)
            if (cRes) {
              resData =
                cRes.data?.case ||
                cRes.data?.task ||
                cRes.data?.data?.case ||
                cRes.data?.data?.task ||
                cRes.data?.data ||
                cRes.data
            }
          }
        }

        // If collection task, ensure full case details (customer, caseNo, userDefinedCaseStatus) are present
        if (this.isCollection && resData) {
          const caseId =
            (typeof resData.case === 'string' ? resData.case : resData.case?._id) ||
            (!resData.tasks ? resData._id : null)
          if (caseId && (!resData.case?.caseNo || !resData.case?.userDefinedCaseStatus)) {
            const extraCaseRes = await getCollectionCaseById({ caseId }).catch(() => null)
            if (extraCaseRes?.data) {
              const extraCase =
                extraCaseRes.data?.case ||
                extraCaseRes.data?.data?.case ||
                extraCaseRes.data?.data ||
                extraCaseRes.data
              if (extraCase && typeof extraCase === 'object') {
                if (typeof resData.case === 'string' || !resData.case) {
                  resData.case = extraCase
                } else {
                  resData.case = { ...extraCase, ...resData.case }
                }
              }
            }
          }
        }

        this.taskData = resData

        const tplRes = await getWhatsappTemplates().catch(() => null)
        if (tplRes) {
          this.whatsappTemplates = tplRes.data?.templates || tplRes.data?.data || []
        }

        // Mark Task as COMPLETED in backend
        if (this.isCollection && this.taskData?.case?._id) {
          await updateCaseStatus({
            caseId: this.taskData.case._id,
            status: 'COMPLETED'
          }).catch((e) => console.warn(e))
        }

        const validTaskId =
          this.taskData?._id ||
          this.taskData?.taskId ||
          this.taskData?.case?.tasks?.[0]?._id ||
          this.taskId

        await updateTaskStatus({
          taskId: validTaskId,
          isCollection: this.isCollection,
          taskStatus: 'COMPLETED',
          actualEndDateTime: dayjs()
        }).catch((e) => console.warn(e))

        // Send final location telemetry
        await sendStepLocation('AppTaskCompleted', this.taskData)
      } catch (err) {
        console.error('Error completing task:', err)
      }
    },
    cleanupTaskLocalStorage() {
      const idList = [
        this.taskId,
        this.taskData?._id,
        this.taskData?.taskId,
        this.taskData?.case?._id,
        this.taskData?.case?.tasks?.[0]?._id
      ].filter(Boolean)

      idList.forEach((id) => {
        localStorage.removeItem(`taskAddress-${id}`)
        localStorage.removeItem(`taskStartTime-${id}`)
        localStorage.removeItem(`lmo-${id}`)
        localStorage.removeItem(`lastMeetingOutcome-${id}`)
        localStorage.removeItem(`lmn-${id}`)
        localStorage.removeItem(`lastMeetingNotes-${id}`)
        localStorage.removeItem(`selfiePath-${id}`)
      })
      localStorage.removeItem('osc')
      localStorage.removeItem('lastOutcomeCategory')
    },
    goToDashboard() {
      this.cleanupTaskLocalStorage()
      this.$router.push('/app/dashboard')
    },
    shareOnWhatsapp() {
      this.whatsappMessage = this.buildWhatsappMessage()
      this.shareModalOpen = true
    },
    sendWhatsappMessage() {
      const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(this.whatsappMessage)}`
      window.open(url, '_blank')
      this.shareModalOpen = false
    },
    copyWhatsappMessage() {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.whatsappMessage)
        this.$toast?.success?.('Message copied to clipboard!')
      }
    },
    async sendCustomerTemplate() {
      if (!this.selectedTemplateId) return
      try {
        this.sendingTemplate = true
        await sendWhatsappNotification({
          caseId: this.taskData?.case?._id || this.taskId,
          templateId: this.selectedTemplateId
        })
        this.$toast?.success?.('WhatsApp notification sent to customer!')
        this.templateModalOpen = false
      } catch (err) {
        console.error('Error sending whatsapp notification:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to send WhatsApp message')
      } finally {
        this.sendingTemplate = false
      }
    }
  }
}
</script>
